import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { CloseOutlined, FilterOutlined } from '@ant-design/icons';
import {
  InputSearch,
  InputSelect,
  SelectOption,
  SimpleOptionChain,
  FilterValueChain,
} from '@/components/_inputs';
import { InputGroup } from '@/components/InputGroup';
import { Popover, DatePicker, Radio, RadioChangeEvent } from 'antd';
import { useTranslation } from 'react-i18next';
import { localeEn, localeRu, localeUA } from '@/config/locales';
import { useAppSelector } from '@/redux/hooks';
import { serviceOptionsSelectors } from '@/redux/selectors';
import { AmlStatusEnum } from '@/api';
import { capitalize } from 'lodash';
import { Button } from '@/components/Button';
import { ChainOptions } from '@/config';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';

import styles from './TableTools.module.sass';

dayjs.extend(customParseFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);

interface TableToolsProps {
  loading: boolean;
  onSearchChangeHandler: (newVal: string) => void;
  onSearchHandler: () => void;
  term: string;
  filters: Record<string, any>;
  onFiltersChangeHandler: (key: string, val: any) => void;
  onApplyHandler: () => void;
  onClearHandler: () => void;
}

const DISPLAY_FORMAT = 'DD.MM.YYYY';

const TableTools: FC<TableToolsProps> = ({
  loading,
  onSearchChangeHandler,
  onSearchHandler,
  term,
  filters,
  onFiltersChangeHandler,
  onApplyHandler,
  onClearHandler,
}) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const [blockchains, setBlockchains] = useState<SelectOption[]>([]);
  const currentLocale = useMemo(() => {
    switch (i18n.languages[0]) {
      case 'ru':
        return localeRu;
      case 'ua':
        return localeUA;
      default:
        return localeEn;
    }
  }, [t]);
  const { data: serviceOptions } = useAppSelector(
    serviceOptionsSelectors.getServiceOptionsSelectors,
  );

  const statusOptions: SelectOption[] = useMemo(() => {
    return [AmlStatusEnum.PENDING, AmlStatusEnum.SUCCESS].map((opt) => {
      return {
        value: opt,
        label: capitalize(t(`translation.status_${opt}`) || ''),
      };
    });
  }, [t]);

  const filterIndicator = useMemo(() => {
    return Object.keys(filters).filter((key) => !!filters[key]).length > 0;
  }, [filters]);

  const earlierThanFrom = (currentDate: any) => {
    if (currentDate && filters.createFrom) {
      return currentDate < dayjs(filters.createFrom);
    }
    return false;
  };

  useEffect(() => {
    if (!!filters.createFrom && !!filters.createTo) {
      if (earlierThanFrom(dayjs(filters.createTo))) {
        onFiltersChangeHandler('createTo', '');
      }
    }
  }, [filters.createFrom]);

  useEffect(() => {
    if (serviceOptions) {
      const chainOptions = ChainOptions.filter(
        (opt) => !serviceOptions?.notInvestigatedCoins?.includes(opt.value),
      );

      setBlockchains(chainOptions);
    }
  }, [serviceOptions]);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const handleApply = () => {
    setOpen((prev) => false);
    onApplyHandler();
  };

  const handleClear = () => {
    setOpen((prev) => false);
    onClearHandler();
  };

  const handleCreated = (values: any, newVal: string, field: string) => {
    onFiltersChangeHandler(field, newVal ? dayjs(newVal, DISPLAY_FORMAT) : '');
  };

  const onStatusChange = (newVal: string) => {
    onFiltersChangeHandler('status', newVal);
  };

  const handleAssetChange = (newVal: string) => {
    onFiltersChangeHandler('asset', newVal);
  };

  return (
    <div className={styles.tools}>
      <Popover
        trigger="click"
        open={open}
        onOpenChange={handleOpenChange}
        content={
          <div ref={popoverContentRef}>
            <div className={styles.close}>
              <CloseOutlined
                className={styles.iconButton}
                onClick={() => handleOpenChange(false)}
              />
            </div>
            <InputGroup
              label={t('translation.filter_label_created')}
              className={styles.label}
            >
              <DatePicker
                locale={currentLocale}
                format={DISPLAY_FORMAT}
                value={filters.createFrom}
                getPopupContainer={() =>
                  popoverContentRef.current || document.body
                }
                onChange={(values, formatString) => {
                  const singleFormatString = Array.isArray(formatString)
                    ? formatString[0]
                    : formatString;

                  handleCreated(values, singleFormatString, 'createFrom');
                }}
              />
              &nbsp;
              <DatePicker
                locale={currentLocale}
                format={DISPLAY_FORMAT}
                value={filters.createTo}
                getPopupContainer={() =>
                  popoverContentRef.current || document.body
                }
                onChange={(values, formatString) => {
                  const singleFormatString = Array.isArray(formatString)
                    ? formatString[0]
                    : formatString;

                  handleCreated(values, singleFormatString, 'createTo');
                }}
                disabledDate={earlierThanFrom}
              />
            </InputGroup>
            <InputGroup
              label={t('translation.specify_network')}
              className={styles.label}
            >
              <InputSelect
                id="status"
                name="status"
                className={styles.statusSelect}
                value={filters.asset}
                onChange={handleAssetChange}
                onBlur={() => {}}
                options={blockchains}
                clearable
                components={{
                  Option: SimpleOptionChain,
                  SingleValue: FilterValueChain,
                }}
              />
            </InputGroup>
            <InputGroup
              label={t('translation.tpl_inv_status_label')}
              className={styles.label}
            >
              <InputSelect
                id="status"
                name="status"
                className={styles.statusSelect}
                value={filters.status}
                onChange={onStatusChange}
                onBlur={() => {}}
                options={statusOptions}
                clearable
              />
            </InputGroup>
            <div className={styles.actions}>
              <Button type="primary" size="s" onClick={handleApply}>
                {t('translation.btn_search_title')}
              </Button>
              <Button type="orange" size="s" onClick={handleClear}>
                {t('translation.btn_clear')}
              </Button>
            </div>
          </div>
        }
      >
        <FilterOutlined
          className={`${styles.iconButton} ${filterIndicator && styles.active}`}
        />
      </Popover>
      <InputSearch
        id="queryHash"
        name="queryHash"
        value={term}
        onChange={onSearchChangeHandler}
        onSearch={onSearchHandler}
        readOnly={loading}
        placeholder="address"
        expandable
      />
    </div>
  );
};

export default TableTools;
